import React, { useEffect, useMemo, useState } from "react";
import { useApiClient } from "../../service/API";
import {
  listCompanyDomains,
  createCompanyDomain,
  updateCompanyDomain,
  retireCompanyDomain }
from "./companyDomainApi"
import CompanyDomainForm from "./CompanyDomainForm";

function extractErrorMessage(err) {
  if (!err) return "Request failed";

  // New normalized API error
  if (err.message) {
    return err.message;
  }

  // Validation errors
  if (Array.isArray(err.validationError) && err.validationError.length > 0) {
    return err.validationError.join(", ");
  }

  // Field validation errors
  if (err.errors && typeof err.errors === "object") {
    return Object.values(err.errors).flat().join(", ");
  }

  return "Request failed";
}

export default function CompanyDomainsAdminPage() {
  const apiClient = useApiClient();

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState("create"); // create | edit
  const [editing, setEditing] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");

  async function refresh() {
    setLoadingList(true);
    setError("");
    setSuccess("");
    try {
      const data = await listCompanyDomains(apiClient);
      setItems(data || []);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((x) =>
      (x.name || "").toLowerCase().includes(q) ||
      (x.company || "").toLowerCase().includes(q) ||
      (x.ticketHeaderPrefix || "").toLowerCase().includes(q) ||
      (x.status || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  async function handleCreate(payload) {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await createCompanyDomain(apiClient, payload);
      setSuccess("Created successfully.");
      await refresh();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(payload) {
    if (!editing?.id) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateCompanyDomain(apiClient, editing.id, payload);
      setSuccess("Updated successfully.");
      setMode("create");
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleRetire(id) {
    setError("");
    setSuccess("");
    try {
      await retireCompanyDomain(apiClient, id);
      setSuccess("Domain retired successfully.");
      await refresh();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-1">Company Domains</h3>
          <div className="text-muted">Admin management (Create / Edit / Retire)</div>
        </div>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={refresh}
          disabled={loadingList}
        >
          {loadingList ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <div className="fw-semibold">Error</div>
          <div>{error}</div>
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <div className="row g-4">
        {/* Left: table */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="fw-semibold">Domain List</div>

              <div className="input-group" style={{ maxWidth: 360 }}>
                <span className="input-group-text">Search</span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="domain / company / prefix / status"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="card-body p-0">
              {loadingList ? (
                <div className="p-3">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-3 text-muted">No domains found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 70 }}>ID</th>
                        <th>Domain</th>
                        <th>Company</th>
                        <th style={{ width: 110 }}>Prefix</th>
                        <th style={{ width: 110 }}>Status</th>
                        <th style={{ width: 110 }}>Retired</th>
                        <th style={{ width: 170 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((x) => (
                        <tr key={x.id}>
                          <td>{x.id}</td>
                          <td className="fw-semibold">{x.name}</td>
                          <td>{x.company}</td>
                          <td>
                            <span className="badge text-bg-secondary">{x.ticketHeaderPrefix}</span>
                          </td>
                          <td>
                            {x.status === "ACTIVE" ? (
                              <span className="badge text-bg-success">ACTIVE</span>
                            ) : (
                              <span className="badge text-bg-secondary">INACTIVE</span>
                            )}
                          </td>
                          <td>
                            {x.retiredAt ? (
                              <span className="badge text-bg-warning">YES</span>
                            ) : (
                              <span className="badge text-bg-light border">NO</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setMode("edit");
                                  setEditing(x);
                                  setError("");
                                  setSuccess("");
                                }}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                disabled={x.status !== "ACTIVE"}
                                onClick={() => handleRetire(x.id)}
                              >
                                Retire
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card-footer text-muted small">
              Showing {filtered.length} of {items.length}
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="col-12 col-lg-5">
          <CompanyDomainForm
            mode={mode}
            initialValue={mode === "edit" ? editing : null}
            loading={saving}
            onSubmit={mode === "create" ? handleCreate : handleUpdate}
            onCancel={() => {
              setMode("create");
              setEditing(null);
            }}
          />
        </div>
      </div>
    </div>
  );
}
